import { supabase } from '@/lib/supabase';
import { Review, ReviewFilters, ReviewStatistics } from '@/types/models';
import { Database } from '@/types/database.types';

type ReviewRow = Database['public']['Tables']['reviews']['Row'];
type ReviewInsert = Database['public']['Tables']['reviews']['Insert'];
type ReviewUpdate = Database['public']['Tables']['reviews']['Update'];
type ReviewImageRow = Database['public']['Tables']['review_images']['Row'];
type ReviewImageInsert = Database['public']['Tables']['review_images']['Insert'];

// Convert database snake_case to application camelCase
const convertReviewFromDb = (row: ReviewRow & { 
  user?: { id: string; full_name: string; email: string; role: string; avatar_url?: string };
  device?: { id: string; name: string; model: string; image_url?: string };
  review_images?: ReviewImageRow[];
}): Review => ({
  id: row.id,
  userId: row.user_id,
  deviceId: row.device_id,
  userDeviceId: row.user_device_id,
  rating: row.rating,
  title: row.title,
  comment: row.comment,
  isVerifiedPractitioner: row.is_verified_practitioner,
  isSuccessStory: row.is_success_story,
  status: row.status,
  adminNotes: row.admin_notes,
  helpfulCount: row.helpful_count,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  approvedAt: row.approved_at,
  approvedBy: row.approved_by,
  user: row.user ? {
    id: row.user.id,
    fullName: row.user.full_name,
    email: row.user.email,
    role: row.user.role,
    avatarUrl: row.user.avatar_url
  } : undefined,
  device: row.device ? {
    id: row.device.id,
    name: row.device.name,
    model: row.device.model,
    imageUrl: row.device.image_url
  } : undefined,
  images: row.review_images?.map(img => ({
    id: img.id,
    reviewId: img.review_id,
    imageUrl: img.image_url,
    caption: img.caption,
    displayOrder: img.display_order,
    createdAt: img.created_at
  }))
});

// Convert application camelCase to database snake_case for insert
const convertReviewToDb = (review: Omit<Review, 'id' | 'createdAt' | 'updatedAt' | 'helpfulCount' | 'approvedAt' | 'approvedBy'>): ReviewInsert => {
  // ONLY include user-editable fields - let database handle defaults for admin fields
  const insertData: ReviewInsert = {
    user_id: review.userId,
    device_id: review.deviceId,
    rating: review.rating,
    title: review.title,
    comment: review.comment
  };

  // Optional user-editable fields
  if (review.userDeviceId !== null && review.userDeviceId !== undefined) {
    insertData.user_device_id = review.userDeviceId;
  }
  
  if (review.isSuccessStory !== null && review.isSuccessStory !== undefined) {
    insertData.is_success_story = review.isSuccessStory;
  }

  // NEVER include these admin-only fields - they have database defaults:
  // - status (defaults to 'pending')
  // - is_verified_practitioner (defaults to false)
  // - admin_notes (defaults to null)
  // - approved_by (defaults to null)
  // - approved_at (defaults to null)

  return insertData;
};

export const reviewService = {
  // Get reviews with filters and sorting
  async getReviews(filters?: ReviewFilters): Promise<{ data: Review[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          user:user_profiles!reviews_user_id_fkey(id, full_name, email, role, avatar_url),
          device:devices(id, name, model, image_url),
          review_images(*)
        `);

      // Apply filters
      if (filters?.deviceId) {
        query = query.eq('device_id', filters.deviceId);
      }
      if (filters?.rating) {
        query = query.eq('rating', filters.rating);
      }
      if (filters?.isVerifiedPractitioner !== undefined) {
        query = query.eq('is_verified_practitioner', filters.isVerifiedPractitioner);
      }
      if (filters?.isSuccessStory !== undefined) {
        query = query.eq('is_success_story', filters.isSuccessStory);
      }
      // Only filter by status if explicitly provided
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      // Apply sorting
      if (filters?.sortBy === 'rating') {
        query = query.order('rating', { ascending: false });
      } else if (filters?.sortBy === 'helpful') {
        query = query.order('helpful_count', { ascending: false });
      } else {
        // Default to recent
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;
      
      if (error) {
        // Return Supabase error with detailed message
        return { 
          data: null, 
          error: new Error(`Database error: ${error.message}${error.hint ? ` (Hint: ${error.hint})` : ''}`) 
        };
      }
      
      return { 
        data: data?.map(convertReviewFromDb) ?? [], 
        error: null 
      };
    } catch (error: any) {
      // Handle network or unexpected errors
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          data: null, 
          error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please check your Supabase dashboard.') 
        };
      }
      
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error(`Unexpected error: ${String(error)}`) 
      };
    }
  },

  // Get single review by ID
  async getReviewById(reviewId: string): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          user:user_profiles!reviews_user_id_fkey(id, full_name, email, role, avatar_url),
          device:devices(id, name, model, image_url),
          review_images(*)
        `)
        .eq('id', reviewId)
        .single();

      if (error) throw error;

      return { 
        data: data ? convertReviewFromDb(data) : null, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch review') 
      };
    }
  },

  // Create new review
  async createReview(
    review: Omit<Review, 'id' | 'createdAt' | 'updatedAt' | 'helpfulCount' | 'approvedAt' | 'approvedBy'>,
    images?: Array<{ imageUrl: string; caption?: string; displayOrder: number }>
  ): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const insertData = convertReviewToDb(review);
      
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .insert(insertData)
        .select(`
          *,
          user:user_profiles!reviews_user_id_fkey(id, full_name, email, role, avatar_url),
          device:devices(id, name, model, image_url)
        `)
        .single();

      if (reviewError) throw reviewError;

      // Insert images if provided
      if (images && images.length > 0 && reviewData) {
        const imageInserts: ReviewImageInsert[] = images.map(img => ({
          review_id: reviewData.id,
          image_url: img.imageUrl,
          caption: img.caption ?? null,
          display_order: img.displayOrder
        }));

        const { error: imagesError } = await supabase
          .from('review_images')
          .insert(imageInserts);

        if (imagesError) throw imagesError;
      }

      // Fetch complete review with images
      return await this.getReviewById(reviewData.id);
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to create review') 
      };
    }
  },

  // Update review
  async updateReview(
    reviewId: string,
    updates: Partial<Pick<Review, 'title' | 'comment' | 'rating' | 'isSuccessStory'>>
  ): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {};
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.comment !== undefined) updateData.comment = updates.comment;
      if (updates.rating !== undefined) updateData.rating = updates.rating;
      if (updates.isSuccessStory !== undefined) updateData.is_success_story = updates.isSuccessStory;

      const { error } = await supabase
        .from('reviews')
        .update(updateData)
        .eq('id', reviewId);

      if (error) throw error;

      return await this.getReviewById(reviewId);
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to update review') 
      };
    }
  },

  // Delete review
  async deleteReview(reviewId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { 
        error: error instanceof Error ? error : new Error('Failed to delete review') 
      };
    }
  },

  // Toggle helpful vote
  async toggleHelpfulVote(reviewId: string, userId: string): Promise<{ isHelpful: boolean; error: Error | null }> {
    try {
      // Check if vote exists
      const { data: existingVote, error: checkError } = await supabase
        .from('review_helpful_votes')
        .select('id')
        .eq('review_id', reviewId)
        .eq('user_id', userId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      if (existingVote) {
        // Remove vote
        const { error: deleteError } = await supabase
          .from('review_helpful_votes')
          .delete()
          .eq('review_id', reviewId)
          .eq('user_id', userId);

        if (deleteError) throw deleteError;

        return { isHelpful: false, error: null };
      } else {
        // Add vote
        const { error: insertError } = await supabase
          .from('review_helpful_votes')
          .insert({ review_id: reviewId, user_id: userId });

        if (insertError) throw insertError;

        return { isHelpful: true, error: null };
      }
    } catch (error) {
      return { 
        isHelpful: false, 
        error: error instanceof Error ? error : new Error('Failed to toggle helpful vote') 
      };
    }
  },

  // Get review statistics for a device
  async getDeviceStatistics(deviceId: string): Promise<{ data: ReviewStatistics | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('review_statistics')
        .select('*')
        .eq('device_id', deviceId)
        .single();

      if (error) {
        // If no statistics found, return null (not an error)
        if (error.code === 'PGRST116') {
          return { data: null, error: null };
        }
        
        return { 
          data: null, 
          error: new Error(`Statistics error: ${error.message}`) 
        };
      }

      return {
        data: data ? {
          deviceId: data.device_id,
          deviceName: data.device_name,
          totalReviews: data.total_reviews,
          verifiedPractitionerReviews: data.verified_practitioner_reviews,
          successStories: data.success_stories,
          averageRating: data.average_rating,
          fiveStarCount: data.five_star_count,
          fourStarCount: data.four_star_count,
          threeStarCount: data.three_star_count,
          twoStarCount: data.two_star_count,
          oneStarCount: data.one_star_count
        } : null,
        error: null
      };
    } catch (error: any) {
      // Handle network errors
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          data: null, 
          error: new Error('Cannot connect to database to fetch statistics') 
        };
      }
      
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch statistics') 
      };
    }
  },

  // Check if user has voted helpful on a review
  async hasUserVotedHelpful(reviewId: string, userId: string): Promise<{ hasVoted: boolean; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('review_helpful_votes')
        .select('id')
        .eq('review_id', reviewId)
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      return { hasVoted: !!data, error: null };
    } catch (error) {
      return { 
        hasVoted: false, 
        error: error instanceof Error ? error : new Error('Failed to check vote status') 
      };
    }
  },

  // Admin: Approve review
  async approveReview(reviewId: string, adminId: string, adminNotes?: string): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {
        status: 'approved',
        approved_by: adminId,
        approved_at: new Date().toISOString(),
        admin_notes: adminNotes ?? null
      };

      const { error } = await supabase
        .from('reviews')
        .update(updateData)
        .eq('id', reviewId);

      if (error) throw error;

      return await this.getReviewById(reviewId);
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to approve review') 
      };
    }
  },

  // Admin: Reject review
  async rejectReview(reviewId: string, adminId: string, adminNotes: string): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {
        status: 'rejected',
        approved_by: adminId,
        approved_at: new Date().toISOString(),
        admin_notes: adminNotes
      };

      const { error } = await supabase
        .from('reviews')
        .update(updateData)
        .eq('id', reviewId);

      if (error) throw error;

      return await this.getReviewById(reviewId);
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to reject review') 
      };
    }
  },

  // Admin: Flag review for further review
  async flagReview(reviewId: string, adminId: string, flagReason: string): Promise<{ data: Review | null; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {
        admin_notes: flagReason
      };

      const { error } = await supabase
        .from('reviews')
        .update(updateData)
        .eq('id', reviewId);

      if (error) throw error;

      return await this.getReviewById(reviewId);
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to flag review') 
      };
    }
  },

  // Admin: Get all review statistics
  async getAllStatistics(): Promise<{ data: ReviewStatistics[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('review_statistics')
        .select('*')
        .order('total_reviews', { ascending: false });

      if (error) throw error;

      return {
        data: data?.map(stat => ({
          deviceId: stat.device_id,
          deviceName: stat.device_name,
          totalReviews: stat.total_reviews,
          verifiedPractitionerReviews: stat.verified_practitioner_reviews,
          successStories: stat.success_stories,
          averageRating: stat.average_rating,
          fiveStarCount: stat.five_star_count,
          fourStarCount: stat.four_star_count,
          threeStarCount: stat.three_star_count,
          twoStarCount: stat.two_star_count,
          oneStarCount: stat.one_star_count
        })) ?? null,
        error: null
      };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch all statistics') 
      };
    }
  },

  // Admin: Bulk approve reviews
  async bulkApproveReviews(reviewIds: string[], adminId: string): Promise<{ successCount: number; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {
        status: 'approved',
        approved_by: adminId,
        approved_at: new Date().toISOString()
      };

      const { error, count } = await supabase
        .from('reviews')
        .update(updateData)
        .in('id', reviewIds);

      if (error) throw error;

      return { successCount: count ?? 0, error: null };
    } catch (error) {
      return { 
        successCount: 0, 
        error: error instanceof Error ? error : new Error('Failed to bulk approve reviews') 
      };
    }
  },

  // Admin: Bulk reject reviews
  async bulkRejectReviews(reviewIds: string[], adminId: string, rejectionReason: string): Promise<{ successCount: number; error: Error | null }> {
    try {
      const updateData: ReviewUpdate = {
        status: 'rejected',
        approved_by: adminId,
        approved_at: new Date().toISOString(),
        admin_notes: rejectionReason
      };

      const { error, count } = await supabase
        .from('reviews')
        .update(updateData)
        .in('id', reviewIds);

      if (error) throw error;

      return { successCount: count ?? 0, error: null };
    } catch (error) {
      return { 
        successCount: 0, 
        error: error instanceof Error ? error : new Error('Failed to bulk reject reviews') 
      };
    }
  }
};